import { Card } from "@/components/ui/card";
import { ProgressCircleManySegments } from "@/components/ui/charts/progress-circle-many-segments";
import { Skeleton } from "@/components/ui/skeleton";
import { useCurrentUserProfile } from "@/hooks/current-user-profile.hook";
import { useQuizById } from "@/hooks/quiz-by-id.hook";
import { usePerformanceDetailReport } from "@/hooks/reports/performance-detail-report.hook";
import { cn } from "@/lib/utils";
import { mapQuestionnaireResultStatusToLabel } from "@/mappers/results";
import { FC, HTMLAttributes } from "react";
import { useParams } from "react-router-dom";

type CompanyResultCardProps = HTMLAttributes<HTMLDivElement>;

const CompanyResultCard: FC<CompanyResultCardProps> = ({
  className,
  ...props
}) => {
  const { data: userProfile } = useCurrentUserProfile();

  const { quizId } = useParams();

  const { data: quiz } = useQuizById({
    companyId: userProfile?.companyId || "",
    applicationQuizId: quizId || "",
  });

  const { data: report, isLoading: isReportLoading } =
    usePerformanceDetailReport({
      companyId: userProfile?.companyId || "",
      applicationQuizId: quiz?.id || "",
    });

  return (
    <Card className={cn(className, "rounded-xl px-5 py-3")} {...props}>
      <p className="mb-1 mt-2 text-xl font-bold text-[#13171B]">
        Resultado de tu empresa
      </p>

      <p className="mb-4 text-sm font-light text-[#8090A3]">
        Para ahondar en los criterios de puntaje, descarga el reporte completo.
      </p>

      {report && (
        <div className="flex flex-col items-center">
          <div className="mb-5 flex flex-col items-center">
            <ProgressCircleManySegments
              radius={90}
              strokeWidth={22}
              segments={[
                {
                  color: "#F1F4F9",
                  value: Number((100 - report.score).toFixed(2)),
                },
                {
                  color: report.color,
                  value: report.score,
                },
              ]}
            >
              <div className="flex flex-col items-center">
                <p className="font-semibold text-black">{report.score}</p>
                <p className="font-semibold text-black">puntos</p>
              </div>
            </ProgressCircleManySegments>
          </div>

          <p className="text-center text-lg font-semibold text-black">
            {mapQuestionnaireResultStatusToLabel(report.resultStatus)}
          </p>

          <div className="mt-4 flex w-[160px] items-center gap-3">
            <div
              className="h-3 w-3 rounded-full border"
              style={{ borderColor: report.color }}
            />
            <p className="text-sm font-semibold text-[#99B2C6]">
              Puntaje {quiz?.edition.year}
            </p>
          </div>

          <div className="mb-2 mt-2 flex w-[160px] items-center gap-3">
            <div className="h-3 w-3 rounded-full border border-[#F1F4F9]" />
            <p className="text-sm font-semibold text-[#99B2C6]">Total</p>
          </div>
        </div>
      )}

      {isReportLoading && (
        <Skeleton
          style={{ width: "200px" }}
          className="m-auto aspect-square rounded-full"
        />
      )}
    </Card>
  );
};

export default CompanyResultCard;
